"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/observability/report-client-error";

function reportToGTM(metric: {
  name: string;
  value: number;
  rating: string;
  delta: number;
  navigationType?: string;
}) {
  if (typeof window === "undefined" || !window.dataLayer) return;

  window.dataLayer.push({
    event: "web-vitals",
    webVitalsMetricName: metric.name,
    webVitalsMetricValue: Math.round(metric.value * 100) / 100,
    webVitalsMetricRating: metric.rating,
    webVitalsMetricDelta: Math.round(metric.delta * 100) / 100,
    webVitalsMetricNavigationType: metric.navigationType ?? "unknown",
    webVitalsMetricPath: window.location.pathname,
  });
}

/**
 * WebVitalsReporter — reports Core Web Vitals to GTM.
 *
 * Amplitude Web Vitals are handled natively via `autocapture.webVitals: true`
 * in the Amplitude SDK init config (lib/analytics/amplitude.ts).
 *
 * Safely handles contexts where next/web-vitals is unavailable (e.g. 404 pages)
 * by catching import/hook errors instead of crashing (NODE-1WV).
 */
export function WebVitalsReporter() {
  useEffect(() => {
    // Dynamic import avoids the ReferenceError on special render contexts
    // (e.g. PUT /_not-found) where the next/web-vitals module is unavailable.
    import("next/web-vitals")
      .then(({ useReportWebVitals: _useReportWebVitals }) => {
        return import("web-vitals").then((webVitals) => {
          webVitals.onTTFB(reportToGTM);
          webVitals.onFCP(reportToGTM);
          webVitals.onLCP(reportToGTM);
          webVitals.onCLS(reportToGTM);
          webVitals.onINP(reportToGTM);
        });
      })
      .catch((err) => {
        // next/web-vitals or web-vitals module unavailable on special pages (e.g. 404)
        console.warn("[WebVitalsReporter] Web vitals module unavailable:", err);
        reportClientError(err, {
          source: "component.WebVitalsReporter",
          feature: "analytics",
          action: "loadWebVitalsModule",
          component: "WebVitalsReporter",
          handled: true,
          severity: "warning",
        });
      });
  }, []);

  return null;
}
